import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useUiStore } from '../store/ui';
import { useBrandingStore } from '../store/branding';
import { http, setAccessToken } from '../lib/api';
import { disconnectSocket } from '../lib/ws';

export interface NavItem {
  to: string;
  label: string;
  icon: string | React.ReactNode;
}
export interface NavGroup {
  section?: string;
  active: string;
  items: NavItem[];
}

/** Syncs the ui-store theme to <html data-theme> so tokens actually switch. */
export function useThemeSync() {
  const theme = useUiStore((s) => s.theme);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
}

function initials(value: string | undefined): string {
  if (!value) return '?';
  const name = value.split('@')[0].replace(/[._-]+/g, ' ').trim();
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function BrandMark() {
  return (
    <span className="brand-mark" aria-hidden>
      <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
        <path d="M8.5 15.5a5 5 0 0 1 0-7M15.5 8.5a5 5 0 0 1 0 7M5.6 18.4a9 9 0 0 1 0-12.8M18.4 5.6a9 9 0 0 1 0 12.8" />
      </svg>
    </span>
  );
}

function Sidebar({
  navGroups,
  brand,
  variant,
  footer,
}: {
  navGroups: NavGroup[];
  brand: string;
  variant?: 'admin';
  footer?: React.ReactNode;
}) {
  const branding = useBrandingStore();
  const name = branding.platformName || brand;
  return (
    <aside className={`sidebar ${variant === 'admin' ? 'sidebar-admin' : ''}`} aria-label="Primary">
      <div className="sidebar-brand">
        {branding.logoUrl ? (
          <img src={branding.logoUrl} alt="" className="brand-logo" />
        ) : (
          <BrandMark />
        )}
        <span className="brand-name">{name}</span>
        {variant === 'admin' ? <span className="brand-badge">Admin</span> : null}
      </div>
      {navGroups.map((group) => (
        <nav key={group.section ?? group.active} className="sidebar-nav">
          {group.section ? <div className="sidebar-section">{group.section}</div> : null}
          {group.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            >
              <span className="nav-icon" aria-hidden>{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      ))}
      <div className="sidebar-footer">
        {footer}
        {footer && branding.footerText?.trim() ? (
          <div className="sidebar-footer-text">{branding.footerText.trim()}</div>
        ) : null}
        {!footer && (branding.footerText?.trim() || `${name} · v0.1.0`)}
      </div>
    </aside>
  );
}

/** Compact signed-in card for the admin sidebar footer. */
function AdminUserCard({ onLogout }: { onLogout: () => void }) {
  const auth = useAuthStore();
  return (
    <div className="sidebar-user">
      <div className="sidebar-user-info">
        <span className="sidebar-user-avatar" aria-hidden>{initials(auth.user?.email)}</span>
        <div className="sidebar-user-meta">
          <span className="sidebar-user-name">{auth.user?.email}</span>
          <span className="sidebar-user-role">{auth.user?.role ?? 'Admin'}</span>
        </div>
      </div>
      <button className="icon-button sidebar-user-logout" title="Log out" onClick={onLogout} aria-label="Log out">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
      </button>
    </div>
  );
}

export function AppLayout(props: {
  brand: string;
  navGroups: NavGroup[];
  children: React.ReactNode;
  variant?: 'admin';
}) {
  const auth = useAuthStore();
  const ui = useUiStore();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<unknown[]>([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  useThemeSync();

  const loadNotifications = async () => {
    try {
      const data = await http.get<{ items: unknown[]; unread: number }>('/api/notifications');
      setNotifications(data.items);
      ui.setUnread(data.unread);
    } catch {
      /* no-op */
    }
  };

  void loadNotifications;

  const logout = async () => {
    try {
      await http.post('/api/auth/logout', {});
    } catch {
      /* ignore */
    }
    auth.clear();
    disconnectSocket();
    setAccessToken(null);
    navigate('/login');
  };

  return (
    <div className={`layout-app ${props.variant === 'admin' ? 'admin-layout' : ''} ${mobileOpen ? 'sidebar-open' : ''}`}>
      <Sidebar
        navGroups={props.navGroups}
        brand={props.brand}
        variant={props.variant}
        footer={props.variant === 'admin' ? <AdminUserCard onLogout={() => void logout()} /> : undefined}
      />
      {mobileOpen ? (
        <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} aria-hidden />
      ) : null}
      <div className="main">
        <header className="topbar">
          <div className="topbar-context">
            <button
              className="icon-button mobile-menu-button"
              aria-label="Toggle navigation"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((open) => !open)}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <span className="text-muted hide-mobile">{auth.tenant?.name ?? 'Dashboard'}</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="icon-button notif-bell"
              aria-label={`Notifications, ${ui.unreadNotifications} unread`}
              onClick={() => void loadNotifications()}
              title="Notifications"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.7 21a2 2 0 0 1-3.4 0" />
              </svg>
              {ui.unreadNotifications > 0 ? <span className="notif-dot">{ui.unreadNotifications}</span> : null}
            </button>
            {notifications.length > 0 ? (
              <span className="text-muted text-xs" onClick={() => setNotifications([])}>
                {notifications.length} new
              </span>
            ) : null}
            <button className="icon-button" aria-label="Toggle theme" onClick={() => ui.toggleTheme()} title="Toggle theme">
              {ui.theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <span className="user-chip" title={auth.user?.email}>
              <span className="avatar" aria-hidden>{initials(auth.user?.email)}</span>
              <span className="user-email">{auth.user?.email}</span>
            </span>
            <button className="btn secondary sm" onClick={() => void logout()}>
              Log out
            </button>
          </div>
        </header>
        <main className="content" onClick={() => mobileOpen && setMobileOpen(false)}>
          {props.children}
        </main>
      </div>
    </div>
  );
}

export function AuthLayout(props: { children: React.ReactNode; title: string; subtitle?: string }) {
  const ui = useUiStore();
  const branding = useBrandingStore();
  useThemeSync();
  return (
    <div className="layout-auth">
      <div className="auth-card">
        <div className="auth-theme-toggle">
          <button className="icon-button" aria-label="Toggle theme" onClick={() => ui.toggleTheme()}>
            {ui.theme === 'dark' ? '☀️' : '🌙'}
          </button>
        </div>
        <div className="flex items-center gap-2 mb-2">
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt="" className="brand-logo" />
          ) : (
            <BrandMark />
          )}
          <h1 style={{ margin: 0 }}>{branding.platformName || 'LiveStreamX'}</h1>
        </div>
        <h2>{props.title}</h2>
        {props.subtitle ? <p className="text-muted text-sm">{props.subtitle}</p> : null}
        <div style={{ marginTop: '1rem' }}>{props.children}</div>
        {branding.footerText?.trim() ? (
          <p className="text-muted text-sm" style={{ marginTop: '1rem' }}>{branding.footerText.trim()}</p>
        ) : null}
      </div>
    </div>
  );
}